import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';

import { getApiBaseUrl } from '../../utils/apiConfig';

// Ensure we always hit the /api namespace even if REACT_APP_API_URL is just the host
const getApiUrl = () => {
  const baseUrl = getApiBaseUrl();
  return `${baseUrl.replace(/\/$/, '')}/api`;
};

interface PressRelease {
  id: string;
  title: string;
  content: string;
}

interface Campaign {
  id: string;
  name: string;
}

interface DistributionInfo {
  distribution: {
    id: string;
    status: string;
    publishedURL: string | null;
  };
  pressRelease: PressRelease;
  campaign: Campaign | null;
  relatedPressReleases: PressRelease[];
}

const MediaPublishedUrl: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const distributionId = searchParams.get('distributionId') || '';

  const [distributionInfo, setDistributionInfo] = useState<DistributionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState('');
  const [campaignUrls, setCampaignUrls] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchDistributionInfo = async () => {
      if (!distributionId) {
        setError('Missing distribution identifier. Please use the link from your email.');
        setLoading(false);
        return;
      }

      try {
        // Add timeout to prevent hanging (8 seconds - reduced for faster feedback)
        const response = await axios.get(`${getApiUrl()}/distribution/url-submission/${distributionId}`, {
          timeout: 8000, // 8 second timeout
          headers: {
            'Cache-Control': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (response.data.success) {
          setDistributionInfo(response.data);
          // Initialize campaign URLs if it's a campaign
          if (response.data.campaign && response.data.relatedPressReleases && response.data.relatedPressReleases.length > 1) {
            const initialUrls: Record<string, string> = {};
            response.data.relatedPressReleases.forEach((pr: PressRelease) => {
              initialUrls[pr.id] = '';
            });
            setCampaignUrls(initialUrls);
          } else if (response.data.pressRelease) {
            // Single PR - initialize single URL field
            setUrl('');
          }
        } else {
          setError('Failed to load distribution information.');
        }
      } catch (err: any) {
        console.error('Error fetching distribution info:', err);
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          setError('Request timed out. Please try again or contact support.');
        } else {
          setError(err?.response?.data?.message || 'Failed to load distribution information. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDistributionInfo();
  }, [distributionId]);

  const handleUrlChange = (prId: string, value: string) => {
    setCampaignUrls(prev => ({
      ...prev,
      [prId]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!distributionId) {
      setError('Missing distribution identifier. Please use the link from your email.');
      return;
    }

    // Check if it's a campaign with multiple PRs
    const isCampaign = distributionInfo?.campaign && 
                       distributionInfo.relatedPressReleases && 
                       distributionInfo.relatedPressReleases.length > 1;

    if (isCampaign) {
      // Validate all URLs are provided
      const urlsToSubmit = distributionInfo.relatedPressReleases
        .map(pr => ({
          prId: pr.id,
          url: campaignUrls[pr.id]?.trim() || ''
        }))
        .filter(item => item.url);

      if (urlsToSubmit.length === 0) {
        setError('Please enter at least one valid URL.');
        return;
      }

      // Validate all URLs are valid
      const invalidUrls = urlsToSubmit.filter(item => 
        !item.url.startsWith('http://') && !item.url.startsWith('https://')
      );

      if (invalidUrls.length > 0) {
        setError('All URLs must start with http:// or https://');
        return;
      }

      try {
        setSubmitting(true);
        const response = await axios.post(
          `${getApiUrl()}/distribution/update-url`,
          {
            distributionId,
            publishedURLs: urlsToSubmit
          },
          {
            timeout: 15000, // 15 second timeout for multiple URLs
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: false // Don't send credentials for public endpoint
          }
        );

        if (response.data && response.data.success) {
          const successCount = response.data.results?.filter((r: any) => r.success).length || urlsToSubmit.length;
          setSuccess(`Thank you! Successfully submitted ${successCount} URL(s).`);
          // Clear URLs after successful submission
          const clearedUrls: Record<string, string> = {};
          Object.keys(campaignUrls).forEach(key => {
            clearedUrls[key] = '';
          });
          setCampaignUrls(clearedUrls);
        } else {
          setError(response.data?.message || 'Failed to submit URLs. Please try again.');
        }
      } catch (err: any) {
        console.error('Error submitting published URLs:', err);
        let message = 'Failed to submit URLs. Please try again or contact support.';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          message = 'Request timed out. Some URLs may have been saved. Please refresh the page or contact support.';
        } else if (err.response) {
          message = err.response.data?.message || `Server error: ${err.response.status}. Please try again.`;
        } else if (err.request) {
          if (err.message?.includes('CORS') || err.message?.includes('Access-Control')) {
            message = 'Network error: CORS issue. Please contact support with your URLs.';
          } else {
            message = 'Network error: Could not reach server. Please check your connection and try again.';
          }
        }
        
        setError(message);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Single URL submission (backward compatible)
      if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) {
        setError('Please enter a valid URL starting with http:// or https://');
        return;
      }

      try {
        setSubmitting(true);
        const response = await axios.post(
          `${getApiUrl()}/distribution/update-url`,
          {
            distributionId,
            publishedURL: url
          },
          {
            timeout: 10000, // 10 second timeout
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: false // Don't send credentials for public endpoint
          }
        );

        if (response.data && response.data.success) {
          setSuccess('Thank you! Your published URL has been recorded.');
          // Clear the URL field after successful submission
          setUrl('');
        } else {
          setError(response.data?.message || 'Failed to submit URL. Please try again.');
        }
      } catch (err: any) {
        console.error('Error submitting published URL:', err);
        let message = 'Failed to submit URL. Please try again or contact support.';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          message = 'Request timed out. The URL may have been saved. Please refresh the page or contact support.';
        } else if (err.response) {
          // Server responded with error
          message = err.response.data?.message || `Server error: ${err.response.status}. Please try again.`;
        } else if (err.request) {
          // Request made but no response (network error or CORS)
          if (err.message?.includes('CORS') || err.message?.includes('Access-Control')) {
            message = 'Network error: CORS issue. Please contact support with your URL.';
          } else {
            message = 'Network error: Could not reach server. Please check your connection and try again.';
          }
        }
        
        setError(message);
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 2,
          background: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)'
        }}
      >
        <CircularProgress sx={{ color: '#ff5200' }} />
        <Typography variant="body1" sx={{ color: 'white' }}>
          Loading distribution information...
        </Typography>
      </Box>
    );
  }

  // Check if it's a campaign with multiple PRs
  const isCampaign = distributionInfo?.campaign && 
                     distributionInfo.relatedPressReleases && 
                     distributionInfo.relatedPressReleases.length > 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #09273b 0%, #0927eb 100%)',
        p: 2
      }}
    >
      <Paper
        elevation={4}
        sx={{
          maxWidth: isCampaign ? 800 : 520,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '2px solid #09273b',
          background: 'white'
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, mb: 1, textAlign: 'center', color: '#09273b' }}
        >
          Submit Published Article URL{isCampaign ? 's' : ''}
        </Typography>
        {isCampaign && distributionInfo?.campaign && (
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, textAlign: 'center', color: '#4A5568', fontWeight: 600 }}
          >
            Campaign: {distributionInfo.campaign.name}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{ mb: 3, textAlign: 'center', color: '#4A5568', fontSize: '15px', lineHeight: 1.6 }}
        >
          {isCampaign
            ? 'Please paste the live URLs of the articles where each press release was published. You can submit URLs for all press releases or just the ones that have been published.'
            : 'Please paste the live URL of the article where this press release was published. This helps CorpCrunch and the brand track performance and analytics.'}
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {isCampaign && distributionInfo?.relatedPressReleases ? (
            <>
              {distributionInfo.relatedPressReleases.map((pr, index) => (
                <Box key={pr.id} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: '#09273b', fontWeight: 600 }}
                  >
                    {index + 1}. {pr.title || 'Press Release'}
                  </Typography>
                  <TextField
                    label="Published Article URL"
                    placeholder="https://example.com/your-article"
                    fullWidth
                    value={campaignUrls[pr.id] || ''}
                    onChange={(e) => handleUrlChange(pr.id, e.target.value)}
                    margin="normal"
                    autoComplete="off"
                    helperText={pr.title && pr.title.length > 60 ? pr.title.substring(0, 60) + '...' : (pr.title || 'Enter the published URL for this press release')}
                  />
                  {index < distributionInfo.relatedPressReleases.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              ))}
            </>
          ) : (
            <TextField
              label="Published Article URL"
              placeholder="https://example.com/your-article"
              fullWidth
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              margin="normal"
              autoComplete="off"
            />
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              mt: 2,
              textTransform: 'none',
              fontWeight: 700,
              py: 1.5,
              backgroundColor: '#ff5200',
              '&:hover': {
                backgroundColor: '#e64a00',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#666'
              },
              fontSize: '16px'
            }}
          >
            {submitting ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ mr: 1, color: 'inherit' }}
                />
                Submitting...
              </>
            ) : (
              `Submit URL${isCampaign ? 's' : ''}`
            )}
          </Button>
        </Box>

        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 2, color: '#4A5568', fontSize: '13px', textAlign: 'center' }}
        >
          If you have any issues, please contact the CorpCrunch team using the
          details in your email.
        </Typography>
      </Paper>
    </Box>
  );
};

export default MediaPublishedUrl;
